import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useMeBootstrap } from '@/hooks/useMeBootstrap';
import { useSession } from '@/state/session';

/**
 * Wraps player-only routes. Mirrors AdminGuard's query-gated pattern (read
 * `query.data?.user ?? storeUser`, only redirect when the bootstrap has
 * actually CONFIRMED an anonymous session — never on initial 'unknown'
 * state or while the first /me fetch is in flight).
 *
 * Approval status is intentionally NOT enforced here. Unapproved users
 * see the dashboard with a "pending approval" panel instead of join CTAs.
 */
export function PlayerGuard({ children }: { children: ReactNode }) {
  const query = useMeBootstrap();
  const storeUser = useSession((s) => s.user);
  const location = useLocation();

  const user = query.data?.user ?? storeUser;

  if (!query.isFetched && !storeUser) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-5">
        <p className="text-cabinet-cream/60">Loading…</p>
      </main>
    );
  }

  if (!user) {
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  return <>{children}</>;
}
